import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  Chip,
  Grid,
  Divider,
  Paper,
  Autocomplete
} from '@mui/material';
import { useMQTT } from '../../hooks/useMQTT';
import { getSelectedRfidDevice, saveSelectedRfidDevice } from '../../utils/rfidDeviceUtils';
import barcodeWorkerService from '../../services/barcodeWorkerService';

/**
 * Demo component to test the Barcode Worker Service
 * This component demonstrates how the background worker automatically
 * injects barcode data into focused input fields
 */
const BarcodeWorkerDemo = () => {
  const [testInput1, setTestInput1] = useState('');
  const [testInput2, setTestInput2] = useState('');
  const [autocompleteValue, setAutocompleteValue] = useState(null);
  const [workerStatus, setWorkerStatus] = useState({});
  const [handheldDevice, setHandheldDevice] = useState('');
  const [testDeviceId, setTestDeviceId] = useState('Handheld device 001');

  // Sample options for autocomplete
  const autocompleteOptions = [
    { label: 'Product A - 1234567890', value: '1234567890' },
    { label: 'Product B - 0987654321', value: '0987654321' },
    { label: 'Product C - 1122334455', value: '1122334455' },
    { label: 'Product D - 5566778899', value: '5566778899' },
    { label: 'Product E - 9988776655', value: '9988776655' }
  ];
  
  const { isConnected, subscribedTopics } = useMQTT();

  // Update worker status periodically
  useEffect(() => {
    const updateStatus = () => {
      setWorkerStatus(barcodeWorkerService.getStatus());
    };

    updateStatus();
    const interval = setInterval(updateStatus, 1000);
    return () => clearInterval(interval);
  }, []);

  // Load current handheld device setting
  useEffect(() => {
    const device = getSelectedRfidDevice('audit');
    setHandheldDevice(device);
  }, []);

  // Save handheld device setting
  const handleSaveDevice = () => {
    const success = saveSelectedRfidDevice(testDeviceId, 'audit');
    if (success) {
      setHandheldDevice(testDeviceId);
      // Force worker to check settings again
      setTimeout(() => {
        setWorkerStatus(barcodeWorkerService.getStatus());
      }, 1000);
    }
  };

  // Test the worker
  const handleTestWorker = () => {
    barcodeWorkerService.test();
  };

  // Simulate barcode input for testing
  const handleSimulateBarcode = () => {
    const testBarcode = '1234567890';
    if (barcodeWorkerService.getStatus().currentFocusedElement) {
      barcodeWorkerService.injectBarcodeToFocusedInput(testBarcode);
    } else {
      alert('Please focus on an input field first, then click this button to simulate barcode input.');
    }
  };

  // Reset debounce mechanism
  const handleResetDebounce = () => {
    barcodeWorkerService.resetDebounce();
  };

  // Clear handheld device setting
  const handleClearDevice = () => {
    const success = saveSelectedRfidDevice('', 'audit');
    if (success) {
      setHandheldDevice('');
      setTestDeviceId('Handheld device 001');
      setTimeout(() => {
        setWorkerStatus(barcodeWorkerService.getStatus());
      }, 1000);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Barcode Worker Service Demo
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        This demo shows how the background worker automatically injects barcode data 
        from MQTT messages into focused input fields.
      </Typography>

      <Grid container spacing={3}>
        {/* Configuration Section */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Configuration
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Current Handheld Device:
                </Typography>
                {handheldDevice ? (
                  <Chip 
                    label={handheldDevice} 
                    color="success" 
                    variant="outlined"
                    sx={{ mb: 1 }}
                  />
                ) : (
                  <Chip 
                    label="No device configured" 
                    color="warning" 
                    variant="outlined"
                    sx={{ mb: 1 }}
                  />
                )}
              </Box>

              <Box sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  label="Test Device ID"
                  value={testDeviceId}
                  onChange={(e) => setTestDeviceId(e.target.value)}
                  size="small"
                  sx={{ mb: 1 }}
                />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button 
                    variant="contained" 
                    onClick={handleSaveDevice}
                    size="small"
                  >
                    Save Device
                  </Button>
                  <Button 
                    variant="outlined" 
                    onClick={handleClearDevice}
                    size="small"
                  >
                    Clear Device
                  </Button>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  MQTT Status:
                </Typography>
                <Chip 
                  label={isConnected ? 'Connected' : 'Disconnected'} 
                  color={isConnected ? 'success' : 'error'} 
                  variant="outlined"
                  sx={{ mb: 1 }}
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Subscribed Topics:
                </Typography>
                {subscribedTopics.length > 0 ? (
                  subscribedTopics.map((topic, index) => (
                    <Chip 
                      key={index}
                      label={topic} 
                      color="primary" 
                      variant="outlined"
                      size="small"
                      sx={{ mr: 1, mb: 1 }}
                    />
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No topics subscribed
                  </Typography>
                )}
              </Box>

              <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <Button 
                  variant="outlined" 
                  onClick={handleTestWorker}
                  sx={{ flex: 1, minWidth: 120 }}
                >
                  Test Worker
                </Button>
                <Button 
                  variant="contained" 
                  onClick={handleSimulateBarcode}
                  sx={{ flex: 1, minWidth: 120 }}
                >
                  Simulate Barcode
                </Button>
                <Button 
                  variant="outlined" 
                  color="warning"
                  onClick={handleResetDebounce}
                  sx={{ flex: 1, minWidth: 120 }}
                >
                  Reset Debounce
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Test Inputs Section */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Test Input Fields
              </Typography>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Focus on any input field below and send a barcode message to the 
                MQTT topic "handheld/test/barcode" to see automatic injection.
              </Typography>

              <Box sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  label="Test Input 1"
                  value={testInput1}
                  onChange={(e) => setTestInput1(e.target.value)}
                  placeholder="Focus here and scan a barcode..."
                  sx={{ mb: 2 }}
                />
                
                <TextField
                  fullWidth
                  label="Test Input 2"
                  value={testInput2}
                  onChange={(e) => setTestInput2(e.target.value)}
                  placeholder="Or focus here and scan a barcode..."
                  sx={{ mb: 2 }}
                />

                <Autocomplete
                  fullWidth
                  options={autocompleteOptions}
                  getOptionLabel={(option) => option.label}
                  value={autocompleteValue}
                  onChange={(event, newValue) => {
                    setAutocompleteValue(newValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Autocomplete Test"
                      placeholder="Focus here and scan a barcode to test autocomplete..."
                    />
                  )}
                  sx={{ mb: 2 }}
                />
              </Box>

              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>How to test:</strong><br />
                  1. Configure a handheld device above<br />
                  2. Focus on one of the input fields (including the autocomplete)<br />
                  3. Send a message to MQTT topic "handheld/test/barcode"<br />
                  4. The barcode data will be typed character-by-character (HID simulation)<br />
                  5. For autocomplete, it should trigger the dropdown and search functionality as you type<br />
                  6. Watch the typing animation - each character appears with realistic timing
                </Typography>
              </Alert>
            </CardContent>
          </Card>
        </Grid>

        {/* Worker Status Section */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Worker Status
              </Typography>
              
              <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="subtitle2">Running:</Typography>
                    <Chip 
                      label={workerStatus.isRunning ? 'Yes' : 'No'} 
                      color={workerStatus.isRunning ? 'success' : 'error'} 
                      size="small"
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="subtitle2">Current Topic:</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {workerStatus.currentTopic || 'None'}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="subtitle2">MQTT Connected:</Typography>
                    <Chip 
                      label={workerStatus.mqttConnected ? 'Yes' : 'No'} 
                      color={workerStatus.mqttConnected ? 'success' : 'error'} 
                      size="small"
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="subtitle2">Focused Element:</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {workerStatus.currentFocusedElement 
                        ? `${workerStatus.currentFocusedElement.tagName} (${workerStatus.currentFocusedElement.type || 'text'})`
                        : 'None'
                      }
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="subtitle2">Typing Status:</Typography>
                    <Chip 
                      label={workerStatus.isTyping ? 'Typing' : 'Idle'} 
                      color={workerStatus.isTyping ? 'warning' : 'success'} 
                      size="small"
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="subtitle2">Last Barcode:</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {workerStatus.lastBarcodeData || 'None'}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BarcodeWorkerDemo;
